<template>
  <Teleport to="body">
    <div class="notification-toast-container">
      <TransitionGroup name="toast-slide">
        <div v-for="notification in visibleNotifications" :key="notification.id"
          class="notification-toast card shadow-lg rounded-5 p-3" @click="handleToastClick(notification)">

          <div class="d-flex gap-3 align-items-start">
            <!-- Avatar -->
            <div class="position-relative flex-shrink-0">
              <img v-if="isModerationNotification(notification.type)" src="/logo.svg" alt="1tik"
                class="rounded-circle object-fit-cover shadow-sm border rounded-circle"
                style="width: 40px; height: 40px; padding: 4px; background: var(--color-bg-card); border-radius: 50%;" />
              <div v-else-if="isSportsNotification(notification.type)"
                class="rounded-circle d-flex align-items-center justify-content-center shadow-sm border"
                style="width: 40px; height: 40px; background: var(--bs-info-bg-subtle, #cfe2ff);">
                <i class="ph ph-soccer-ball" style="font-size: 20px; color: var(--bs-info, var(--bs-info));"></i>
              </div>
              <div v-else-if="isLiveNotificationType(notification.type)"
                class="rounded-circle d-flex align-items-center justify-content-center shadow-sm border"
                style="width: 40px; height: 40px; background: #fee2e2;">
                <i class="ph ph-broadcast" style="font-size: 20px; color: #ef4444;"></i>
              </div>
              <profilePicture v-else :user="notification.actor" width="40" height="40" />
            </div>

            <!-- Content -->
            <div class="flex-grow-1 overflow-hidden my-auto">
              <p class="mb-0 text-dark lh-1 fs-6">
                <!-- For sports notifications: show content only (no actor) -->
                <template v-if="isSportsNotification(notification.type)">
                <template v-if="isSoccerV2FavoriteTeamNotification(notification)">
                  <span class="fw-bold d-block soccer-v2-headline">
                    {{ soccerDisplay(notification).headline }}
                  </span>
                  <span
                    v-if="soccerDisplay(notification).detail"
                    class="text-muted small d-block soccer-v2-detail"
                  >
                    {{ soccerDisplay(notification).detail }}
                  </span>
                </template>
                  <span v-else class="fw-semibold">
                    {{ getNotificationContent(notification) }}
                  </span>
                </template>
                <!-- For other types: show content with actor name included -->
                <template v-else>
                  <span class="fw-semibold">
                    {{ getNotificationContent(notification) }}
                  </span>
                </template>
              </p>

              <div class="d-flex align-items-center gap-2 mt-1">
                <span
                  :class="{ 'text-primary fw-semibold': !notification.isRead, 'text-muted fw-normal': notification.isRead }">{{
                    timeAgo(notification.createdAt) }}</span>
              </div>
            </div>

            <!-- Close Button -->
            <button class="btn-close flex-shrink-0" @click.stop="dismissToast(notification.id)"
              :aria-label="$t('notifications.close')"></button>
          </div>

        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { notificationStore } from '@/domains/notifications/store/notificationStore'
import { storeToRefs } from 'pinia'
import { timeAgo, displayImage } from '@/shared/utils/generalUtils'
import profilePicture from "@/domains/profile/components/features/profilePicture.vue";
import {
  isSoccerV2FavoriteTeamNotification,
  getSoccerV2NotificationDisplay,
} from '@/domains/soccer-v2/utils/soccer-v2-notification-display'
import { NotificationTypeUtils } from '@/shared/constants/notificationTypes'
import {
  getLiveStreamIdFromNotification,
  getLiveNotificationMessage,
  isLiveNotificationType,
  navigateToLiveFromNotification,
  navigateWithNotificationTargets,
} from '@/shared/utils/notificationUtils'

const router = useRouter()
const store = notificationStore()
const { t } = useI18n()
const { newNotification } = storeToRefs(store)

const MAX_VISIBLE_TOASTS = 5

// State
const visibleNotifications = ref([])
const toastTimeouts = new Map()

// Obtenir l'ID de conversation d'une notification
const getConversationId = (notification) => {
  return notification.conversationId || notification.conversation?.id || notification.data?.conversationId
}

// Ajouter un toast et démarrer le timer
const showToast = (notification) => {
  if (!notification || !notification.id) return

  // Éviter les doublons par ID
  const exists = visibleNotifications.value.some(n => n.id === notification.id)
  if (exists) return

  // Optimisation spéciale pour NEW_MESSAGE : regrouper par conversation
  if (notification.type === 'NEW_MESSAGE') {
    const conversationId = getConversationId(notification)
    
    if (conversationId) {
      // Chercher une notification existante pour la même conversation
      const existingIndex = visibleNotifications.value.findIndex(n => 
        n.type === 'NEW_MESSAGE' && getConversationId(n) === conversationId
      )

      if (existingIndex !== -1) {
        // Mettre à jour la notification existante
        const existingNotification = visibleNotifications.value[existingIndex]
        
        // Incrémenter le compteur de messages
        if (!existingNotification.messageCount) {
          existingNotification.messageCount = 1
        }
        existingNotification.messageCount++
        
        // Mettre à jour l'acteur (au cas où c'est un autre utilisateur dans la même conversation)
        // Garder l'acteur le plus récent
        existingNotification.actor = notification.actor
        
        // Mettre à jour la date de création pour afficher le temps le plus récent
        existingNotification.createdAt = notification.createdAt
        
        // Réinitialiser le timer d'auto-dismiss
        const existingTimeoutId = toastTimeouts.get(existingNotification.id)
        if (existingTimeoutId) {
          clearTimeout(existingTimeoutId)
        }
        
        const newTimeoutId = setTimeout(() => {
          dismissToast(existingNotification.id)
        }, 10000)
        
        toastTimeouts.set(existingNotification.id, newTimeoutId)
        
        // Forcer la réactivité Vue
        visibleNotifications.value[existingIndex] = { ...existingNotification }
        
        return
      }
    }
  }

  // Limiter le nombre de toasts visibles
  if (visibleNotifications.value.length >= MAX_VISIBLE_TOASTS) {
    // Retirer le plus ancien
    const oldest = visibleNotifications.value[0]
    dismissToast(oldest.id)
  }

  // Initialiser le compteur de messages à 1 pour les nouvelles notifications NEW_MESSAGE
  if (notification.type === 'NEW_MESSAGE') {
    notification.messageCount = 1
  }

  // Ajouter le nouveau toast
  visibleNotifications.value.push(notification)

  // Démarrer le timer d'auto-dismiss (DISABLED FOR DEBUGGING)
  const timeoutId = setTimeout(() => {
    dismissToast(notification.id)
  }, 10000)

  toastTimeouts.set(notification.id, timeoutId)
}

// Fermer un toast
const dismissToast = (id) => {
  const index = visibleNotifications.value.findIndex(n => n.id === id)
  if (index !== -1) {
    visibleNotifications.value.splice(index, 1)
  }

  // Nettoyer le timeout
  const timeoutId = toastTimeouts.get(id)
  if (timeoutId) {
    clearTimeout(timeoutId)
    toastTimeouts.delete(id)
  }

}

// Navigation au clic
const handleToastClick = async (notification) => {
  dismissToast(notification.id)

  // Marquer comme lue si nécessaire
  if (!notification.isRead) {
    if (notification.type === 'PAGE_MEMBER_ADDED') {
      notification.isRead = true
      store.unreadCount = Math.max(0, store.unreadCount - 1)
    } else {
      store.markAsRead(notification.id)
    }
  }

  if (notification.type === 'USER_WENT_LIVE') {
    await navigateToLiveFromNotification(notification, router, t)
    return
  }

  // Navigation selon le type de notification
  const sportsRelatedTypes = [
    'FAVORITE_TEAM_MATCH_STARTING',
    'FAVORITE_TEAM_MATCH_STARTED',
    'FAVORITE_TEAM_MATCH_FINISHED',
    'FAVORITE_TEAM_SCORED',
    'FAVORITE_TEAM_WON',
    'FAVORITE_TEAM_LOST',
    'FAVORITE_TEAM_DREW',
    'FAVORITE_TEAM_MATCH_REMINDER',
  ]

  if (sportsRelatedTypes.includes(notification.type)) {
    const targets = NotificationTypeUtils.getNavigationTargets(notification)
    navigateWithNotificationTargets(targets, router)
    return
  }

  const route = getNotificationRoute(notification)
  if (route) {
    router.push(route)
  }
}

// Obtenir la route de navigation selon le type
const getNotificationRoute = (notification) => {
  const postRelatedTypes = ['POST_LIKE', 'COMMENT', 'REACTION_ADDED', 'MENTION', 'REPLY']
  const messageRelatedTypes = ['NEW_MESSAGE', 'MESSAGE', 'CONVERSATION_CREATED']

  // PAGE_MEMBER_ADDED now points to page invitations tab.
  if (notification.type === 'PAGE_MEMBER_ADDED') {
    return { name: 'social.pagelist', query: { tab: 'invites' } }
  }

  // Navigation vers un post
  if (postRelatedTypes.includes(notification.type) ||
    ['POST_SHARED', 'POST_TAGGED', 'GROUP_TAGGED', 'PAGE_TAGGED', 'POST_UPDATED', 'COMMENT_MENTION'].includes(notification.type)) {
    const postId = notification.postId || notification.post?.id ||
      notification.comment?.postId || notification.comment?.post?.id
    if (postId) return `/post/${postId}`
  }

  // Navigation vers une conversation (messages)
  if (messageRelatedTypes.includes(notification.type)) {
    // Check if this is a chat request notification
    const isChatRequest = notification.data?.isChatRequest === true || notification.data?.chatRequestId

    if (isChatRequest) {
      // Redirect to requests tab for chat requests
      return { name: 'chat.chat', query: { tab: 'requests' } }
    }

    const conversationId = notification.conversationId || notification.conversation?.id
    if (conversationId) {
      // Utiliser le nom de la route pour générer l'URL correcte
      return { name: 'chat.chat', params: { conversationId } }
    }
    return { name: 'chat.chat' }
  }

  // Navigation vers un profil (demande d'ami)
  if (notification.type === 'FRIEND_REQUEST' || notification.type === 'FRIEND_REQUEST_ACCEPTED') {
    const actorId = notification.actorId || notification.actor?.id
    if (actorId) return `/friendprofile/${actorId}`
  }

  // Navigation vers un groupe
  const groupTypes = ['GROUP_INVITE_TO_GROUP', 'GROUP_JOIN_REQUEST', 'GROUP_JOIN_REQUEST_ACCEPTED', 'GROUP_POST_CREATED', 'GROUP_MEMBER_ADDED']
  if (groupTypes.includes(notification.type)) {
    const groupId = notification.groupId || notification.group?.id || notification.data?.groupId || notification.data?.group?.id
    if (groupId) {
      // For GROUP_JOIN_REQUEST, navigate to requests tab
      if (notification.type === 'GROUP_JOIN_REQUEST') {
        return { name: 'social.group-detail', params: { groupId }, query: { tab: 'requests' } }
      }
      // For GROUP_POST_CREATED with pending approval, navigate to unapproved-posts tab
      if (notification.type === 'GROUP_POST_CREATED' && notification?.data?.pendingApproval) {
        return { name: 'social.group-detail', params: { groupId }, query: { tab: 'unapproved-posts' } }
      }
      return { name: 'social.group-detail', params: { groupId } }
    }
    return '/group'
  }

  // Navigation vers un live
  if (notification.type === 'USER_WENT_LIVE') {
    const liveStreamId = getLiveStreamIdFromNotification(notification)
    if (liveStreamId) {
      return { name: 'live.viewer.screen', params: { id: liveStreamId } }
    }
    return { name: 'live.feed' }
  }

  // Navigation vers une page
  const pageTypes = [
    'PAGE_FOLLOW',
    'PAGE_MEMBER_ADDED',
    'PAGE_INVITE_TO_FOLLOW',
    'PAGE_INVITE_ACCEPTED',
    'PAGE_MEMBER_ROLE_UPDATED',
    'PAGE_POST_CREATED',
    'PAGE_MEMBER_INVITE_ACCEPTED',
    'PAGE_MEMBER_INVITE_DECLINED'
  ]
  if (pageTypes.includes(notification.type)) {
    const pageId = notification.pageId || notification.page?.id || notification.data?.pageId || notification.data?.page?.id
    if (pageId) return { name: 'social.pageview', params: { id: pageId } }
    return { name: 'social.pagelist' }
  }

  // Navigation vers un événement
  if (notification.type === 'EVENT_CANCELLED') {
    // EVENT_CANCELLED redirige vers la liste des événements
    return { name: 'social.events-list' }
  } else if (['EVENT_INVITATION', 'EVENT_UPDATED', 'EVENT_INVITATION_ACCEPTED'].includes(notification.type)) {
    // Priorité: data.eventId (comme dans la réponse API), puis eventId direct, puis event.id
    const eventId = notification.data?.eventId || notification.eventId || notification.event?.id
    if (eventId) {
      return { name: 'social.event-detail', params: { id: eventId } }
    }
    return { name: 'social.events-list' }
  }

  // Navigation vers une story
  if (['STORY_TAGGED', 'STORY_GROUP_TAGGED', 'STORY_PAGE_TAGGED', 'STORY_REACTION'].includes(notification.type)) {
    const storyId = notification.storyId || notification.story?.id || notification.data?.storyId
    const actorId = notification.actorId || notification.actor?.id
    const groupId = notification.groupId || notification.group?.id || notification.data?.groupId
    const pageId = notification.pageId || notification.page?.id || notification.data?.pageId

    // Gestion spéciale pour STORY_TAGGED : vérifier l'âge de la notification
    if (notification.type === 'STORY_TAGGED') {
      if (actorId) {
        const notificationDate = new Date(notification.createdAt);
        const now = new Date();
        const diffMs = now - notificationDate;
        const diffHours = diffMs / (1000 * 60 * 60);
        const isRecent = diffHours < 24;

        if (isRecent && storyId) {
          // Notification récente (< 24h) : ouvrir la story
          return { name: 'social.friendprofile', params: { id: actorId, storyId } }
        } else {
          // Notification ancienne (>= 24h) : juste rediriger vers le profil
          return { name: 'social.friendprofile', params: { id: actorId } }
        }
      }
    } else if (notification.type === 'STORY_REACTION') {
      // Pour STORY_REACTION : rediriger vers le profil de l'utilisateur connecté (propriétaire de la story)
      if (storyId) {
        return { name: 'social.profilemain', params: { storyId } }
      } else {
        return { name: 'social.profilemain' }
      }
    } else {
      // Gestion normale pour les autres types de stories (STORY_GROUP_TAGGED, STORY_PAGE_TAGGED)
      if (storyId) {
        if (groupId) {
          return { name: 'social.group-detail', params: { groupId } }
        } else if (pageId) {
          return { name: 'social.pageview', params: { id: pageId } }
        } else if (actorId) {
          return { name: 'social.friendprofile', params: { id: actorId, storyId } }
        }
      }
    }
  }

  // Navigation vers demande de famille
  if (notification.type === 'FAMILY_REQUEST') {
    return '/friendrequest'
  }

  // Navigation vers campagne
  if (notification.type === 'CAMPAIGN_FINISHED') {
    const campaignId = notification.campaignId || notification.campaign?.id || notification.data?.campaignId
    if (campaignId) {
      return `/campaign/${campaignId}`
    }
    return '/feed'
  }

  if (notification.type === 'CAREERS_APPLICATION_UPDATED') {
    const applicationId =
      notification.data?.applicationId || notification.applicationId
    return {
      name: 'app.students.applied',
      ...(applicationId ? { query: { applicationId } } : {}),
    }
  }
  if (
    notification.type === 'CAREERS_NEW_APPLICATION' ||
    notification.type === 'CAREERS_APPLICATION_WITHDRAWN'
  ) {
    const pageId =
      notification.data?.pageId ||
      notification.pageId ||
      notification.page?.id
    const opportunityId =
      notification.data?.opportunityId ||
      notification.opportunityId
    if (pageId) {
      return { name: 'social.pageview', params: { id: pageId } }
    }
    if (opportunityId) {
      return {
        name: 'app.students.detail',
        params: { projectId: opportunityId },
      }
    }
    return { name: 'app.students' }
  }

  // Navigation vers les pages soccer/match/team pour les notifications sports
  const sportsRelatedTypes = [
    'FAVORITE_TEAM_MATCH_STARTING',
    'FAVORITE_TEAM_MATCH_STARTED',
    'FAVORITE_TEAM_MATCH_FINISHED',
    'FAVORITE_TEAM_SCORED',
    'FAVORITE_TEAM_WON',
    'FAVORITE_TEAM_LOST',
    'FAVORITE_TEAM_DREW',
    'FAVORITE_TEAM_MATCH_REMINDER'
  ]
  if (sportsRelatedTypes.includes(notification.type)) {
    return null
  }

  // Fallback vers la page des notifications
  return '/notification'
}

// Extraction des données
const isModerationNotification = (type) => {
  const moderationTypes = [
    'POST_PENDING_MODERATION',
    'POST_APPROVED',
    'POST_BANNED',
    'STORY_PENDING_MODERATION',
    'STORY_APPROVED',
    'STORY_BANNED',
    'COMMENT_PENDING_MODERATION',
    'COMMENT_APPROVED',
    'COMMENT_BANNED'
  ]
  return moderationTypes.includes(type)
}

const isSportsNotification = (type) => {
  const sportsTypes = [
    'FAVORITE_TEAM_MATCH_STARTING',
    'FAVORITE_TEAM_MATCH_STARTED',
    'FAVORITE_TEAM_MATCH_FINISHED',
    'FAVORITE_TEAM_SCORED',
    'FAVORITE_TEAM_WON',
    'FAVORITE_TEAM_LOST',
    'FAVORITE_TEAM_DREW',
    'FAVORITE_TEAM_MATCH_REMINDER'
  ]
  return sportsTypes.includes(type)
}

const getActorName = (notification) => {
  const actor = notification.actor
  if (!actor) return t('notifications.user')

  if (actor.firstName && actor.lastName) {
    return `${actor.firstName} ${actor.lastName}`
  }

  return actor.firstName || actor.username || t('notifications.user')
}

const getReactionLabel = (reaction) => {
  if (!reaction) return ''
  const normalized = reaction.toUpperCase ? reaction.toUpperCase() : reaction
  const reactionKey = {
    LIKE: 'reactions.like',
    LOVE: 'reactions.love',
    HAHA: 'reactions.haha',
    WOW: 'reactions.wow',
    SAD: 'reactions.sad',
    ANGRY: 'reactions.angry',
    DISLIKE: 'reactions.dislike'
  }[normalized]
  return reactionKey ? t(reactionKey) : reaction
}

const soccerDisplay = (notification) => getSoccerV2NotificationDisplay(notification, t)

const getNotificationContent = (notification) => {
  if (isSoccerV2FavoriteTeamNotification(notification)) {
    return soccerDisplay(notification).combined
  }

  const { type, reaction, data, actor, messageCount } = notification

  // Extraire userName de la même manière que dans Notification.vue
  const userName = actor?.firstName && actor?.lastName
    ? `${actor.firstName} ${actor.lastName}`
    : actor?.username || t('notifications.someone')

  // Extraire eventName pour les notifications d'événements
  const eventName = data?.eventName || notification.event?.name || ''
  const pageName = data?.pageName || notification.page?.name || ''
  const invitedRole = data?.role || ''
  const translateWithFallback = (key, params, fallback) => {
    const translated = t(key, params)
    return translated === key ? fallback : translated
  }

  const getPageMemberAddedText = () => {
    if (notification?.body) return notification.body
    if (pageName && invitedRole) {
      return translateWithFallback(
        'notifications.messages.PAGE_MEMBER_ADDED_WITH_ROLE',
        { pageName, role: invitedRole },
        `You received a page invitation for "${pageName}" as ${invitedRole}`
      )
    }
    if (pageName) {
      return translateWithFallback(
        'notifications.messages.PAGE_MEMBER_ADDED_WITH_PAGE',
        { pageName },
        `You received a page invitation for "${pageName}"`
      )
    }
    if (invitedRole) {
      return translateWithFallback(
        'notifications.messages.PAGE_MEMBER_ADDED_ROLE_ONLY',
        { role: invitedRole },
        `You received a page invitation as ${invitedRole}`
      )
    }
    return t('notifications.messages.PAGE_MEMBER_ADDED')
  }

  const getPageMemberInviteLifecycleText = () => {
    if (notification?.body) return notification.body
    if (type === 'PAGE_MEMBER_INVITE_ACCEPTED') {
      return t('notifications.messages.PAGE_MEMBER_INVITE_ACCEPTED')
    }
    if (type === 'PAGE_MEMBER_INVITE_DECLINED') {
      return t('notifications.messages.PAGE_MEMBER_INVITE_DECLINED')
    }
    return t('notifications.messages.NEW_NOTIFICATION')
  }
  
  // Extraire groupName pour les notifications de groupe
  const groupName = data?.groupName || notification.group?.name || ''
  const groupNameText = groupName ? ` "${groupName}"` : ''

  const textMap = {
    // Messages & Chat
    POST_LIKE: `${userName} ${t('notifications.messages.POST_LIKE')}`,
    COMMENT: `${userName} ${t('notifications.messages.COMMENT')}`,
    REACTION_ADDED: `${userName} ${reaction ? `${t('notifications.messages.REACTION_ADDED_WITH')} ${getReactionLabel(reaction)}` : t('notifications.messages.REACTION_ADDED')}`,
    MESSAGE_REACTION: `${userName} ${t('notifications.messages.MESSAGE_REACTION')}`,
    MENTION: `${userName} ${t('notifications.messages.MENTION')}`,
    COMMENT_MENTION: `${userName} ${t('notifications.messages.COMMENT_MENTION')}`,
    REPLY: `${userName} ${t('notifications.messages.REPLY')}`,
    NEW_MESSAGE: messageCount && messageCount > 1
      ? `${t('notifications.messages.NEW_MESSAGE')} ${userName} (${messageCount})`
      : `${t('notifications.messages.NEW_MESSAGE')} ${userName}`,
    MESSAGE: `${t('notifications.messages.MESSAGE')} ${userName}`,
    CONVERSATION_CREATED: `${userName} ${t('notifications.messages.CONVERSATION_CREATED')}`,
    USER_ADDED_TO_CONVERSATION: `${userName} ${t('notifications.messages.USER_ADDED_TO_CONVERSATION')}`,
    USER_REMOVED_FROM_CONVERSATION: `${userName} ${t('notifications.messages.USER_REMOVED_FROM_CONVERSATION')}`,
    // Social & Posts
    POST_SHARED: `${userName} ${t('notifications.messages.POST_SHARED')}`,
    POST_TAGGED: `${userName} ${t('notifications.messages.POST_TAGGED')}`,
    GROUP_TAGGED: `${userName} ${t('notifications.messages.GROUP_TAGGED')}`,
    PAGE_TAGGED: `${userName} ${t('notifications.messages.PAGE_TAGGED')}`,
    POST_UPDATED: `${userName} ${t('notifications.messages.POST_UPDATED')}`,
    // Friendship
    FRIEND_REQUEST: `${userName} ${t('notifications.messages.FRIEND_REQUEST')}`,
    FRIEND_REQUEST_ACCEPTED: t('notifications.messages.FRIEND_REQUEST_ACCEPTED', { userName }),
    // Groups
    GROUP_INVITE_TO_GROUP: `${userName} ${t('notifications.messages.GROUP_INVITE_TO_GROUP')}`,
    GROUP_JOIN_REQUEST: `${userName} ${t('notifications.messages.GROUP_JOIN_REQUEST')}${groupNameText}`,
    GROUP_JOIN_REQUEST_ACCEPTED: `${userName} ${t('notifications.messages.GROUP_JOIN_REQUEST_ACCEPTED')}`,
    GROUP_POST_CREATED: notification?.data?.pendingApproval
      ? (() => {
          const extractedGroupName = groupName || notification?.data?.groupName || ''
          // Construire le texte directement dans le composant avec userName
          const baseText = t('notifications.messages.GROUP_POST_CREATED_PENDING')
          // Remplacer les placeholders
          let message = baseText.replace('{{userName}}', userName)
          if (extractedGroupName) {
            message = message.replace('{{groupName}}', extractedGroupName)
          } else {
            message = message.replace('{{groupName}}', '')
          }
          return message
        })()
      : `${userName} ${t('notifications.messages.GROUP_POST_CREATED')}${groupNameText}`,
    GROUP_MEMBER_ADDED: `${userName} ${t('notifications.messages.GROUP_MEMBER_ADDED')}`,
    // Pages
    PAGE_FOLLOW: `${userName} ${t('notifications.messages.PAGE_FOLLOW')}`,
    PAGE_MEMBER_ADDED: getPageMemberAddedText(),
    PAGE_MEMBER_INVITE_ACCEPTED: getPageMemberInviteLifecycleText(),
    PAGE_MEMBER_INVITE_DECLINED: getPageMemberInviteLifecycleText(),
    PAGE_POST_CREATED: `${userName} ${t('notifications.messages.PAGE_POST_CREATED')}`,
    PAGE_INVITE_TO_FOLLOW: `${userName} ${t('notifications.messages.PAGE_INVITE_TO_FOLLOW')}`,
    PAGE_INVITE_ACCEPTED: `${userName} ${t('notifications.messages.PAGE_INVITE_ACCEPTED')}`,
    PAGE_MEMBER_ROLE_UPDATED: `${userName} ${t('notifications.messages.PAGE_MEMBER_ROLE_UPDATED')}`,
    // Events
    EVENT_INVITATION: `${userName} ${t('notifications.messages.EVENT_INVITATION', { eventName })}`,
    EVENT_CANCELLED: t('notifications.messages.EVENT_CANCELLED', { actor: userName, eventName }),
    EVENT_UPDATED: t('notifications.messages.EVENT_UPDATED', { actor: userName }),
    EVENT_INVITATION_ACCEPTED: `${userName} ${t('notifications.messages.EVENT_INVITATION_ACCEPTED')}`,
    // Tasks & Projects
    TASK_ASSIGNED: `${userName} ${t('notifications.messages.TASK_ASSIGNED')}`,
    TASK_COMPLETED: `${userName} ${t('notifications.messages.TASK_COMPLETED')}`,
    TASK_REOPENED: `${userName} ${t('notifications.messages.TASK_REOPENED')}`,
    PROJECT_INVITATION: `${userName} ${t('notifications.messages.PROJECT_INVITATION')}`,
    TASK_UPDATED: `${userName} ${t('notifications.messages.TASK_UPDATED')}`,
    TASK_REMINDER_DAILY: t('notifications.messages.TASK_REMINDER_DAILY'),
    TASK_REMINDER_BEFORE_DEADLINE: t('notifications.messages.TASK_REMINDER_BEFORE_DEADLINE'),
    TASK_REMINDER_CUSTOM: t('notifications.messages.TASK_REMINDER_CUSTOM'),
    // Stories
    STORY_TAGGED: `${userName} ${t('notifications.messages.STORY_TAGGED')}`,
    STORY_GROUP_TAGGED: `${userName} ${t('notifications.messages.STORY_GROUP_TAGGED')}`,
    STORY_PAGE_TAGGED: `${userName} ${t('notifications.messages.STORY_PAGE_TAGGED')}`,
    STORY_REACTION: `${userName} ${t('notifications.messages.STORY_REACTION')}`,
    // Social Features
    FAMILY_REQUEST: `${userName} ${t('notifications.messages.FAMILY_REQUEST')}`,
    CAMPAIGN_FINISHED: t('notifications.messages.CAMPAIGN_FINISHED'),
    // Calls
    VOICE_CALL: `${userName} ${t('notifications.messages.VOICE_CALL')}`,
    VIDEO_CALL: `${userName} ${t('notifications.messages.VIDEO_CALL')}`,
    // Others
    INVITATION: `${t('notifications.messages.INVITATION')} ${userName}`,
    BRONZE_BADGE: t('notifications.messages.BRONZE_BADGE'),
    SILVER_BADGE: t('notifications.messages.SILVER_BADGE'),
    GOLD_BADGE: t('notifications.messages.GOLD_BADGE'),
    // Moderation
    POST_PENDING_MODERATION: t('moderation.postPending'),
    POST_APPROVED: t('moderation.postApproved'),
    POST_BANNED: t('moderation.postBanned'),
    STORY_PENDING_MODERATION: t('moderation.storyPending'),
    STORY_APPROVED: t('moderation.storyApproved'),
    STORY_BANNED: t('moderation.storyBanned'),
    COMMENT_PENDING_MODERATION: t('moderation.commentPending'),
    COMMENT_APPROVED: t('moderation.commentApproved'),
    COMMENT_BANNED: t('moderation.commentBanned'),
    // Sports / Favorite Teams
    FAVORITE_TEAM_MATCH_STARTING: t('notifications.messages.FAVORITE_TEAM_MATCH_STARTING'),
    FAVORITE_TEAM_MATCH_STARTED: t('notifications.messages.FAVORITE_TEAM_MATCH_STARTED'),
    FAVORITE_TEAM_MATCH_FINISHED: t('notifications.messages.FAVORITE_TEAM_MATCH_FINISHED'),
    FAVORITE_TEAM_SCORED: t('notifications.messages.FAVORITE_TEAM_SCORED'),
    FAVORITE_TEAM_WON: t('notifications.messages.FAVORITE_TEAM_WON'),
    FAVORITE_TEAM_LOST: t('notifications.messages.FAVORITE_TEAM_LOST'),
    FAVORITE_TEAM_DREW: t('notifications.messages.FAVORITE_TEAM_DREW'),
    FAVORITE_TEAM_MATCH_REMINDER: t('notifications.messages.FAVORITE_TEAM_MATCH_REMINDER'),
    USER_WENT_LIVE: getLiveNotificationMessage(notification, t),
  }

  let content = textMap[type] || `${t('notifications.messages.NEW_NOTIFICATION')} ${userName}`

  if (['NEW_MESSAGE', 'MESSAGE', 'INVITATION'].includes(type)) {
    return content
  }

  return content
}

const handleImageError = (event) => {
  event.target.src = displayImage(null)
}

// Écouter les nouvelles notifications
watch(newNotification, (notification) => {
  if (notification) {
    showToast(notification)
  }
})

// Cleanup lors de la destruction du composant
onUnmounted(() => {
  // Nettoyer tous les timeouts
  toastTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
  toastTimeouts.clear()
})

// DEBUG: Sample toast
// onMounted(() => {
//   setTimeout(() => {
//     showToast({
//       id: 'debug-1',
//       type: 'COMMENT',
//       actor: {
//         firstName: 'John',
//         lastName: 'Doe',
//         username: 'johndoe',
//         profilePictureUrl: 'https://i.pravatar.cc/150?u=1'
//       },
//       isRead: false,
//       createdAt: new Date().toISOString()
//     })

//     showToast({
//       id: 'debug-2',
//       type: 'POST_LIKE',
//       actor: {
//         firstName: 'Alice',
//         lastName: 'Smith',
//         username: 'alicesmith',
//         profilePictureUrl: 'https://i.pravatar.cc/150?u=2'
//       },
//       isRead: false,
//       createdAt: new Date(Date.now() - 36000000).toISOString() // 1 hour ago
//     })
//   }, 1000)
// })
</script>

<style scoped>
.notification-toast-container {
  position: fixed;
  bottom: 20px;
  left: 30px;
  z-index: 99999;
  display: flex;
  flex-direction: column-reverse;
  /* Newest at bottom */
  gap: 15px;
  width: 24rem;
  max-width: 90vw;
  pointer-events: none;
}

@media (max-width: 768px) {
  .notification-toast-container {
    left: 10px;
    right: 10px;
    max-width: none;
    bottom: 10px;
  }
}

.notification-toast {
  pointer-events: all;
  cursor: pointer;
  transition: all 0.3s ease;
}

.notification-toast:hover {
  transform: translateY(-2px);
}

/* Animations */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-slide-enter-from,
.toast-slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.toast-slide-move {
  transition: transform 0.4s ease;
}

.object-fit-cover {
  object-fit: cover;
}

.soccer-v2-headline,
.soccer-v2-detail {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>
